import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreditCard, Check, X, Sparkles, Tag, Loader2, ChevronRight, Shield } from "lucide-react";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$" };

function PlanCard({ plan, currentPlan, currentStatus, onSelect, currency, discountPct, loading }) {
    const price = currency === "INR" ? plan.price_inr : plan.price_usd;
    const discounted = Math.round(price * (100 - discountPct) / 100);
    const isFree = plan.key === "free";
    const isCurrent = currentPlan === plan.key;
    const isCancelled = isCurrent && currentStatus === "cancelled";
    const showActive = isCurrent && !isCancelled;
    return (
        <div
            data-testid={`plan-card-${plan.key}`}
            className={`relative bg-white border ${plan.popular ? "border-ikb border-2" : "border-border"} p-6 sm:p-7 flex flex-col`}
        >
            {plan.popular && (
                <div className="absolute -top-3 left-6 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                    Most popular
                </div>
            )}
            {showActive && (
                <div className="absolute -top-3 right-6 bg-emerald-600 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                    Current plan
                </div>
            )}
            {isCancelled && (
                <div className="absolute -top-3 right-6 bg-rose-600 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                    Cancelled
                </div>
            )}
            <div className="font-display font-black text-2xl">{plan.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
                {discountPct > 0 && !isFree && price > 0 && (
                    <span className="text-xl text-foreground/40 line-through font-mono">
                        {CURRENCY_SYMBOL[currency]}{price.toLocaleString()}
                    </span>
                )}
                <span className="font-display font-black text-4xl text-ikb">
                    {CURRENCY_SYMBOL[currency]}{(discountPct > 0 && !isFree ? discounted : price).toLocaleString()}
                </span>
                {!isFree && <span className="text-xs font-mono text-foreground/60">/ month</span>}
            </div>
            <ul className="mt-5 space-y-2 text-sm flex-1">
                {(plan.features || []).map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{f}</span>
                    </li>
                ))}
            </ul>
            <Button
                onClick={() => onSelect(plan)}
                disabled={isFree || showActive || loading}
                className={`mt-6 rounded-none ${showActive ? "bg-emerald-600 text-white" : "bg-ikb hover:bg-[#001f7a] text-white"}`}
                data-testid={`plan-select-${plan.key}`}
            >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : showActive ? "Active" : isCancelled ? `Reactivate ${plan.name}` : isFree ? "Starter (default)" : `Upgrade to ${plan.name}`}
            </Button>
        </div>
    );
}

export default function BillingPlans() {
    const [data, setData] = useState(null);
    const [gateways, setGateways] = useState(null);
    const [orders, setOrders] = useState([]);
    const [currency, setCurrency] = useState("INR");
    const [code, setCode] = useState("");
    const [discountPct, setDiscountPct] = useState(0);
    const [discountStatus, setDiscountStatus] = useState(null);
    const [validatingCode, setValidatingCode] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const load = async () => {
        try {
            const [{ data: sub }, { data: gw }, { data: ord }] = await Promise.all([
                api.get("/subscription"),
                api.get("/billing/gateways"),
                api.get("/billing/orders").catch(() => ({ data: { items: [] } })),
            ]);
            setData(sub);
            setGateways(gw);
            setOrders(ord?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    // Mock confirm path: when redirected from /billing/mock-checkout/:id we automatically POST /subscription/confirm
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const confirmId = params.get("confirm");
        if (confirmId) {
            api.post("/subscription/confirm", { checkout_id: confirmId })
                .then(() => { toast.success("Subscription activated"); load(); navigate("/billing", { replace: true }); })
                .catch((err) => toast.error(formatApiError(err)));
        }
        // eslint-disable-next-line
    }, [location.search]);

    const validateCode = async (planKey) => {
        if (!code.trim()) { setDiscountPct(0); setDiscountStatus(null); return; }
        setValidatingCode(true);
        try {
            const { data: res } = await api.post("/discount-codes/validate", { code, plan_key: planKey });
            setDiscountPct(res.percent_off);
            setDiscountStatus({ ok: true, msg: `${res.percent_off}% off applied` });
        } catch (err) {
            setDiscountPct(0);
            setDiscountStatus({ ok: false, msg: formatApiError(err) });
        } finally { setValidatingCode(false); }
    };

    const startCheckout = async (plan) => {
        setLoadingPlan(plan.key);
        try {
            const payload = {
                plan_key: plan.key,
                currency,
                discount_code: code.trim() || null,
                gateway: "cashfree",
                return_url: `${window.location.origin}/billing`,
            };
            const { data: res } = await api.post("/subscription/checkout", payload);
            if (res.integration === "cashfree" && res.payment_session_id) {
                await loadCashfreeJs(res.env);
                if (window.Cashfree) {
                    const cf = window.Cashfree({ mode: (res.env || "TEST") === "PROD" ? "production" : "sandbox" });
                    cf.checkout({ paymentSessionId: res.payment_session_id, redirectTarget: "_self" });
                } else {
                    toast.error("Cashfree SDK failed to load");
                }
            } else if (res.checkout_url) {
                // mock checkout — open same tab
                window.location.href = res.checkout_url;
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoadingPlan(null); }
    };

    const loadCashfreeJs = (env) =>
        new Promise((resolve) => {
            if (window.Cashfree) return resolve();
            const s = document.createElement("script");
            s.src = (env || "TEST") === "PROD" ? "https://sdk.cashfree.com/js/v3/cashfree.js" : "https://sdk.cashfree.com/js/v3/cashfree.js";
            s.onload = () => resolve();
            s.onerror = () => resolve();
            document.head.appendChild(s);
        });

    const cancel = async () => {
        if (!confirm("Cancel your subscription? You will keep access until the end of the current billing period.")) return;
        try {
            await api.post("/subscription/cancel");
            toast.success("Subscription cancelled");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!data) {
        return <div className="max-w-[1600px] mx-auto px-4 py-5 text-sm font-mono text-foreground/60">Loading plans…</div>;
    }

    const cur = data.subscription || { plan_key: "free" };
    const curPlanKey = cur.plan_key || "free";
    const cashfree = gateways?.gateways?.find((g) => g.key === "cashfree");

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Billing</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-3">
                        <CreditCard className="w-8 h-8 text-ikb" /> Subscription
                    </h1>
                    <p className="text-foreground/80 mt-2 text-sm">Pick the plan that fits your hiring volume. Cancel anytime.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex border border-border bg-white" data-testid="currency-toggle">
                        <button onClick={() => setCurrency("INR")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${currency === "INR" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cur-inr">INR ₹</button>
                        <button onClick={() => setCurrency("USD")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-border ${currency === "USD" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="cur-usd">USD $</button>
                    </div>
                </div>
            </div>

            {/* Current subscription */}
            <div className="border border-border bg-white p-4 sm:p-5 flex items-center justify-between flex-wrap gap-3" data-testid="sub-current-card">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Current plan</div>
                    <div className="font-display font-bold text-xl mt-1">
                        {(data.plan?.name) || "Free"}
                        <span className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 ${cur.status === "active" ? "bg-emerald-100 text-emerald-700" : cur.status === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-secondary"}`}>
                            {cur.status || "active"}
                        </span>
                    </div>
                    {cur.current_period_end && (
                        <div className="text-xs text-muted-foreground mt-1">
                            Renews on {new Date(cur.current_period_end).toLocaleDateString()}
                        </div>
                    )}
                    {cur.discount_pct > 0 && (
                        <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {cur.discount_pct}% discount · code {cur.discount_code}</div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {cashfree && (
                        <div className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${cashfree.configured ? "bg-emerald-100 text-emerald-700 border border-emerald-300" : "bg-amber-100 text-amber-700 border border-amber-300"}`}>
                            <Shield className="w-3 h-3 inline mr-1" />
                            Cashfree {cashfree.configured ? cashfree.environment : "not configured"}
                        </div>
                    )}
                    {curPlanKey !== "free" && cur.status !== "cancelled" && (
                        <Button onClick={cancel} variant="outline" className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="sub-cancel">
                            Cancel
                        </Button>
                    )}
                </div>
            </div>

            {/* Discount code */}
            <div className="border border-border bg-white p-4 sm:p-5 flex items-center gap-3 flex-wrap" data-testid="discount-row">
                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-500" /> Have a code?</div>
                <Input
                    value={code}
                    onChange={(e) => { setCode(e.target.value.toUpperCase()); setDiscountPct(0); setDiscountStatus(null); }}
                    placeholder="LAUNCH50"
                    className="rounded-none h-9 max-w-[180px] uppercase font-mono"
                    data-testid="discount-input"
                />
                <Button onClick={() => validateCode("pro")} disabled={!code.trim() || validatingCode} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="discount-apply">
                    {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
                {discountStatus && (
                    <span className={`text-xs inline-flex items-center gap-1 ${discountStatus.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="discount-status">
                        {discountStatus.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {discountStatus.msg}
                    </span>
                )}
            </div>

            {/* Plan grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(data.plans || []).map((p) => (
                    <PlanCard
                        key={p.key}
                        plan={p}
                        currentPlan={curPlanKey}
                        currentStatus={cur.status}
                        currency={currency}
                        discountPct={discountPct}
                        onSelect={startCheckout}
                        loading={loadingPlan === p.key}
                    />
                ))}
            </div>

            {/* Orders history */}
            {orders.length > 0 && (
                <div className="border border-border bg-white" data-testid="orders-history">
                    <div className="border-b border-border px-4 py-3 bg-secondary/40">
                        <h2 className="font-display font-bold text-sm uppercase tracking-widest">Payment history</h2>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                            <tr>
                                <th className="text-left px-4 py-2">Date</th>
                                <th className="text-left px-4 py-2">Plan</th>
                                <th className="text-left px-4 py-2">Amount</th>
                                <th className="text-left px-4 py-2">Status</th>
                                <th className="text-left px-4 py-2">Gateway</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {orders.slice(0, 10).map((o) => (
                                <tr key={o.id} data-testid={`order-row-${o.order_id || o.id}`}>
                                    <td className="px-4 py-2 font-mono text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-2 capitalize">{o.plan_key || o.target_plan}</td>
                                    <td className="px-4 py-2 font-mono">{CURRENCY_SYMBOL[o.currency || "INR"]}{(o.amount || o.amount_inr || 0).toLocaleString()}</td>
                                    <td className="px-4 py-2"><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 ${o.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-secondary"}`}>{o.status}</span></td>
                                    <td className="px-4 py-2 text-xs font-mono">{o.integration || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
